import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import { Link } from 'react-router-dom';
import { ChevronDownIcon, ChevronUpIcon, ExclamationCircleIcon, CheckCircleIcon, ClockIcon } from '@heroicons/react/24/outline';
import ViewsNavigation from './ViewsNavigation';

/**
 * MissingInformation component displays active projects with missing information items.
 * 
 * Features:
 * - Shows projects with at least one missing information item
 * - Excludes completed and canceled projects
 * - Allows filtering by status (pending/received) and visibility (internal/client)
 * - Provides an accordion interface to view all missing items for each project
 */
const MissingInformation = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [projectsWithMissingItems, setProjectsWithMissingItems] = useState([]);
  const [expandedProjects, setExpandedProjects] = useState({});
  const [filterStatus, setFilterStatus] = useState('all'); // 'all', 'pending', 'received'
  const [filterInternal, setFilterInternal] = useState('all'); // 'all', 'internal', 'client'

  useEffect(() => {
    fetchProjectsWithMissingItems();
  }, []);

  const fetchProjectsWithMissingItems = async () => {
    try {
      setLoading(true);
      setError(null);

      // Step 1: Get all projects
      const projectsResponse = await api.get('/projects');
      let projects = Array.isArray(projectsResponse.data) 
        ? projectsResponse.data 
        : projectsResponse.data.projects || [];
      
      // Filter out completed or canceled projects
      projects = projects.filter(project => {
        const status = project.status ? project.status.toLowerCase() : '';
        return !status.includes('complete') && 
               !status.includes('finished') && 
               !status.includes('done') && 
               !status.includes('cancel') && 
               !status.includes('abandon');
      });

      // Step 2: For each project, check if it has missing items
      const projectsWithMissingItemsPromises = projects.map(async project => {
        try {
          const missingItemsResponse = await api.get(`/missing-items/project/${project.id}`);
          const missingItems = missingItemsResponse.data.missing_items || [];
          
          // Only include projects with missing items
          if (missingItems.length > 0) {
            return {
              ...project,
              missingItems
            };
          }
          return null;
        } catch (err) {
          console.error(`Error fetching missing items for project ${project.id}:`, err);
          return null;
        }
      });

      const results = await Promise.all(projectsWithMissingItemsPromises);
      // Filter out null results (projects with no missing items)
      const filteredProjects = results.filter(Boolean);
      
      setProjectsWithMissingItems(filteredProjects);
    } catch (err) {
      console.error('Error fetching projects with missing items:', err);
      setError('Failed to load projects with missing information. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const toggleProject = (projectId) => {
    setExpandedProjects(prev => ({
      ...prev,
      [projectId]: !prev[projectId]
    }));
  };

  // Helper function to format dates
  const formatDate = (dateString) => {
    if (!dateString) return 'No due date';
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };
  
  // Calculate if an item is overdue
  const isOverdue = (dueDate) => {
    if (!dueDate) return false;
    return new Date(dueDate) < new Date();
  };

  // Filter the missing items based on status and internal/client
  const getFilteredProjects = () => {
    return projectsWithMissingItems.map(project => {
      const filteredItems = project.missingItems.filter(item => {
        const statusMatch = filterStatus === 'all' || 
          (filterStatus === 'pending' && item.status === 'pending') ||
          (filterStatus === 'received' && item.status === 'received');
          
        const internalMatch = filterInternal === 'all' || 
          (filterInternal === 'internal' && item.is_internal) ||
          (filterInternal === 'client' && !item.is_internal);
          
        return statusMatch && internalMatch;
      });
      
      // Only return projects that have items matching the current filter
      if (filteredItems.length > 0) {
        return {
          ...project,
          missingItems: filteredItems
        };
      }
      return null;
    }).filter(Boolean); // Remove null values
  };

  const filteredProjects = getFilteredProjects();

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 missing-info-container">
      <div className="py-6">
        <h1 className="text-2xl font-semibold text-gray-900">Missing Information</h1>
        <p className="mt-1 text-sm text-gray-500">View all projects with missing information items</p>
        <p className="mt-1 text-sm text-gray-400 italic">Note: Completed and canceled projects are excluded from this view</p>
      </div>
      
      <ViewsNavigation />
      
      {/* Filters */}
      <div className="mb-6 flex flex-wrap gap-4">
        <div>
          <label htmlFor="filterStatus" className="block text-sm font-medium text-gray-700 mb-1">Status</label>
          <select
            id="filterStatus"
            className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md text-gray-900"
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
          >
            <option value="all">All Statuses</option>
            <option value="pending">Pending</option>
            <option value="received">Received</option>
          </select>
        </div>
        
        <div>
          <label htmlFor="filterInternal" className="block text-sm font-medium text-gray-700 mb-1">Visibility</label>
          <select
            id="filterInternal"
            className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md text-gray-900"
            value={filterInternal}
            onChange={(e) => setFilterInternal(e.target.value)}
          >
            <option value="all">All Items</option>
            <option value="internal">Internal Only</option>
            <option value="client">Client Visible</option>
          </select>
        </div>
      </div>
      
      {/* Loading state */}
      {loading && (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
        </div>
      )}
      
      {/* Error state */}
      {error && (
        <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-6">
          <div className="flex">
            <div className="flex-shrink-0">
              <ExclamationCircleIcon className="h-5 w-5 text-red-400" aria-hidden="true" />
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      )}
      
      {/* No missing items */}
      {!loading && !error && filteredProjects.length === 0 && (
        <div className="bg-white shadow overflow-hidden sm:rounded-md p-6 text-center">
          <p className="text-gray-500">No projects with missing information found.</p>
        </div>
      )}
      
      {/* Projects with missing items list */}
      {!loading && !error && filteredProjects.length > 0 && (
        <div className="bg-white shadow overflow-hidden sm:rounded-md">
          <ul className="divide-y divide-gray-200">
            {filteredProjects.map((project) => (
              <li key={project.id}>
                <div className="px-4 py-4 sm:px-6">
                  <div 
                    className="flex items-center justify-between cursor-pointer"
                    onClick={() => toggleProject(project.id)}
                  >
                    <div className="flex items-center">
                      {expandedProjects[project.id] ? (
                        <ChevronUpIcon className="h-5 w-5 text-gray-400 mr-2" />
                      ) : (
                        <ChevronDownIcon className="h-5 w-5 text-gray-400 mr-2" />
                      )}
                      <div>
                        <h3 className="text-lg font-medium text-indigo-600">
                          <Link to={`/projects/${project.id}`} className="hover:underline" onClick={(e) => e.stopPropagation()}>
                            {project.name}
                          </Link>
                        </h3>
                        <p className="text-sm text-gray-500">
                          <span>
                            {project.client?.name || project.client_name || 'No client'}
                          </span>
                          <span className="mx-2">•</span>
                          <span>
                            {project.missingItems.length} missing item{project.missingItems.length !== 1 ? 's' : ''}
                          </span>
                          <span className="mx-2">•</span>
                          <span>
                            {project.missingItems.filter(item => item.status === 'pending').length} pending
                          </span>
                        </p>
                      </div>
                    </div>
                    <div className="flex-shrink-0">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                        {project.status}
                      </span>
                    </div>
                  </div>
                  
                  {/* Expanded view with missing items */}
                  {expandedProjects[project.id] && (
                    <div className="mt-4 border-t border-gray-100 pt-4">
                      <ul className="space-y-3">
                        {project.missingItems.map((item) => (
                          <li 
                            key={item.id} 
                            className={`p-3 rounded-lg ${
                              item.status === 'received' ? 'bg-green-50' : 
                              isOverdue(item.due_date) && item.status === 'pending' ? 'bg-red-50' : 'bg-gray-50'
                            }`}
                          >
                            <div className="flex justify-between">
                              <div className="flex-1">
                                <p className={`text-sm font-medium ${
                                  item.status === 'received' ? 'line-through text-gray-500' : 'text-gray-900'
                                }`}>
                                  {item.content}
                                </p>
                                <div className="mt-1 flex items-center text-xs text-gray-500">
                                  <span className="inline-flex items-center mr-3">
                                    <ClockIcon className="flex-shrink-0 mr-1 h-4 w-4 text-gray-400" />
                                    {formatDate(item.due_date)}
                                    {isOverdue(item.due_date) && item.status === 'pending' && (
                                      <span className="ml-1 text-xs font-semibold text-red-600">
                                        OVERDUE
                                      </span>
                                    )}
                                  </span>
                                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                                    item.is_internal ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'
                                  }`}>
                                    {item.is_internal ? 'Internal' : 'Client-Visible'}
                                  </span>
                                  <span className="ml-2 text-xs text-gray-500">
                                    Added by {item.created_by_name || 'Team Member'} on {formatDate(item.created_at)}
                                  </span>
                                </div>
                              </div>
                              <div className="ml-4 flex items-center">
                                {item.status === 'pending' ? (
                                  <CheckCircleIcon className="h-5 w-5 text-gray-400" />
                                ) : (
                                  <CheckCircleIcon className="h-5 w-5 text-green-500" />
                                )}
                              </div>
                            </div>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default MissingInformation; 